import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type Theme = 'light' | 'dark';

export type ThemeContextType = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  availableThemes: Theme[];
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export type ThemeProviderProps = {
  children: ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
  themes?: Theme[];
};

export function ThemeProvider({
  children,
  defaultTheme = 'light',
  storageKey = 'hydn-theme',
  themes = ['light', 'dark'],
}: Readonly<ThemeProviderProps>) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(storageKey) as Theme;
      return stored && themes.includes(stored) ? stored : defaultTheme;
    }
    return defaultTheme;
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove(...themes);
    root.classList.add(theme);
  }, [theme, themes]);

  const setTheme = useCallback(
    (newTheme: Theme) => {
      if (themes.includes(newTheme)) {
        localStorage.setItem(storageKey, newTheme);
        const root = window.document.documentElement;
        root.classList.add('no-transitions');
        root.classList.remove(...themes);
        root.classList.add(newTheme);
        setThemeState(newTheme);
        // Remove after one frame so hover/focus transitions still work
        requestAnimationFrame(() => {
          requestAnimationFrame(() => root.classList.remove('no-transitions'));
        });
      } else {
        console.warn(
          `Theme "${newTheme}" is not in availableThemes. Add it to the themes prop.`
        );
      }
    },
    [themes, storageKey]
  );

  const value = useMemo(
    () => ({ theme, setTheme, availableThemes: themes }),
    [theme, themes, setTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeProvider;
